import Reveal from "@/components/Reveal";
import { useSeo } from "@/lib/seo";

const PRINCIPES = [
  "Nous privilégions les bonnes décisions aux opérations à tout prix.",
  "Les meilleures opérations sont parfois celles que nous choisissons de ne pas faire.",
  "L'expérience est notre principal actif.",
  "La confiance se construit lentement et se protège chaque jour.",
  "Nous recherchons des opérations solides avant des marges élevées.",
  "Nous créons des lieux de vie, en gardant les pieds sur terre.",
  "Nous choisissons nos partenaires avec la même exigence que nos opérations.",
  "Nous avançons avec patience, méthode et discipline.",
];

const PAYSAGE = "/brand/cote-atlantique.jpg";

export default function Approche() {
  useSeo({
    title: "Notre approche — Gambit Immobilier",
    description:
      "La méthode de Gambit Immobilier : huit principes fondés sur le terrain, l'analyse, la patience et la qualité des décisions.",
    path: "/approche",
  });
  return (
    <div data-testid="page-approche" className="bg-cream">
      {/* ---------------- Page de garde --------------------------------- */}
      <section className="mx-auto max-w-[1400px] px-6 pb-14 pt-16 md:px-12 md:pb-20 md:pt-24 lg:px-16">
        <Reveal className="col-span-12">
          <p className="kicker mb-8 md:mb-10">Notre approche</p>
          <h1
            data-testid="approche-title"
            className="text-display text-[clamp(2.75rem,7vw,6rem)] text-ink"
          >
            Une méthode, <br />
            <span className="text-gold">huit principes.</span>
          </h1>
        </Reveal>
      </section>

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="hairline" />
      </div>

      {/* ---------------- L'origine ------------------------------------- */}
      <section className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-28 lg:px-16">
        <div className="grid grid-cols-12 gap-y-10 md:gap-x-8">
          <Reveal className="col-span-12 md:col-span-3">
            <p className="kicker">I — Origine</p>
            <h2 className="mt-5 font-serif text-[1.75rem] leading-tight text-ink md:text-[1.875rem]">
              L'origine.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="col-span-12 md:col-span-7 md:col-start-5">
            <div data-testid="approche-origine" className="prose-edito space-y-5 max-w-prose">
              <p>Gambit est née en 2026.</p>
              <p>
                Elle prolonge près de vingt années d'expérience acquises au contact
                du terrain et des opérations immobilières.
              </p>
              <p>Les réussites ont confirmé certaines convictions.</p>
              <p>Les erreurs en ont construit d'autres.</p>
              <p>Aujourd'hui, cette expérience forme une méthode.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- Notre manière de réfléchir -------------------- */}
      <section className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-28 lg:px-16">
        <div className="grid grid-cols-12 gap-y-10 md:gap-x-8">
          <Reveal className="col-span-12 md:col-span-3">
            <p className="kicker">II — Méthode</p>
            <h2 className="mt-5 font-serif text-[1.75rem] leading-tight text-ink md:text-[1.875rem]">
              Notre manière <br /> de réfléchir.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="col-span-12 md:col-span-7 md:col-start-5">
            <div className="prose-edito space-y-5 max-w-prose">
              <p>Chaque décision engage la suite du projet.</p>
              <p>
                C'est pourquoi nous privilégions toujours l'analyse à la
                précipitation, la cohérence à l'opportunité et le long terme au
                résultat immédiat.
              </p>
              <p>
                Les huit principes qui suivent résument cette manière de
                travailler.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- Les huit principes ---------------------------- */}
      <section className="bg-cream-dark/40">
        <div className="mx-auto max-w-[1400px] px-6 py-16 md:px-12 md:py-24 lg:px-16">
          <Reveal>
            <p className="kicker">III — Principes</p>
            <h2 className="mt-5 font-serif text-[1.875rem] leading-[1.1] text-ink md:text-[2.25rem]">
              Les huit principes.
            </h2>
          </Reveal>

          <ol
            data-testid="approche-principes"
            className="mt-12 grid grid-cols-1 gap-x-10 md:mt-16 md:grid-cols-2"
          >
            {PRINCIPES.map((text, idx) => (
              <Reveal key={idx} delay={(idx % 2) * 0.06} as="li">
                <div
                  data-testid={`principe-${idx + 1}`}
                  className="group grid grid-cols-12 gap-x-5 border-t border-ink/15 py-7 transition-colors duration-300 hover:border-gold md:py-8"
                >
                  <span className="col-span-2 font-serif text-[1.125rem] text-gold transition-transform duration-500 group-hover:translate-x-1 md:col-span-2">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <p className="col-span-10 font-serif text-[1.125rem] leading-snug text-ink md:col-span-10 md:text-[1.25rem]">
                    {text}
                  </p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>
    </div>
  );
}
